/**
 * What an AUTHOR is editing, read from the address rather than from the
 * cascade (#2009).
 *
 * ── why this is not `useExperienceAtHost` ─────────────────────────────────
 *
 * `core.get_experience` RESOLVES: it answers what applies at an address, which
 * for an address with no row of its own is somebody else's layer. A composer
 * fed that document presents a borrowed layer as this target's own, and Save
 * then freezes a copy of it into the target's key — shadowing the layer it was
 * inheriting, with nothing erroring. Both earlier attempts at #2009 did this.
 *
 * `core.form_layout_at_target` answers about the ADDRESS: whether a row exists
 * there, whether that row is the one that applies, the effective document with
 * its provenance, and the layer that would apply if this one were removed.
 */
import { useQuery } from '@tanstack/react-query'
import { useSolum } from './client'

export type AuthoringHost = 'console' | 'portal'

/** One layer as the ladder reports it. */
export interface AuthoringLayer {
  document: unknown
  rung: string | null
  scope: string | null
  source_key: string | null
  persona: string | null
  host: string | null
  editable?: boolean
}

export interface AuthoringTarget {
  contractVersion: number
  entity: string
  persona: string | null
  host: AuthoringHost
  /** a row EXISTS at this address — Save edits rather than creates, Reset applies */
  owned_here: boolean
  /**
   * The row itself, or null. This is what the EDITOR opens.
   *
   * Distinct from `effective`, and the distinction only shows at a shadowed
   * address: there `effective` is the layer that outranks this one, so opening
   * it would put somebody else's document under this address's name and Save
   * would publish it here. `status` travels because an author editing a
   * superseded row is entitled to know that is what they are doing.
   */
  owned: {
    document: unknown
    scope: string | null
    status: string
    version: number
    source_key: string
    updated_at: string
  } | null
  /** the key both write doors derive for this address — Save upserts it, Reset deletes it */
  key: string
  /** that row is the one that APPLIES; false with `owned_here` means shadowed */
  effective_is_here: boolean
  effective: AuthoringLayer | null
  /** what would apply if this address's row were removed — the Reset preview */
  lower: AuthoringLayer | null
}

/**
 * WHICH DOCUMENT THE COMPOSER OPENS at one authoring target.
 *
 * The row at this address first, then the one it inherits — and nothing else.
 * Named and exported because the choice is a rule, and a rule buried in a JSX
 * expression cannot be mutated, tested, or argued with.
 *
 * The order is the whole point at a SHADOWED address, the only place the two
 * differ: there `effective` is the layer that OUTRANKS this one. Opening it
 * would put somebody else's document under this address's name, and Save would
 * publish it here as this address's own — the clobber, arriving by a second
 * route after the door closed the first.
 *
 * There is deliberately no third term. A caller that gets `null` falls back to
 * its GENERATED form, not to another layer: if this address's own document
 * cannot be read, showing a different address's document in its place is the
 * error this function exists to prevent, not a recovery from it.
 */
export function authoringDocument(target: AuthoringTarget | undefined | null): unknown {
  if (!target) return null
  return target.owned?.document ?? target.effective?.document ?? null
}

/**
 * Cache address for one authoring target.
 *
 * `null` is an ADDRESS here, not an omission — it names the deployment default
 * — so it stays in the key. Collapsing it would let the deployment target reuse
 * a persona's cached document, which is the same confusion the door exists to
 * end, moved into the client.
 */
export const authoringTargetQueryKey = (
  entity: string, persona: string | null, host: AuthoringHost,
) => ['experience', 'authoring-target', entity, persona, host] as const

export function useAuthoringTarget(
  entity: string, persona: string | null, host: AuthoringHost, enabled = true,
) {
  const { client } = useSolum()
  return useQuery<AuthoringTarget>({
    queryKey: authoringTargetQueryKey(entity, persona, host),
    enabled,
    /* Not retried. The door refuses a non-administrator with 42501, and that is
       an ANSWER rather than a failure: #2009's ruling is that a refusal means no
       definition controls. Retrying it would delay the controls disappearing and
       make an ordinary member's console look briefly like an author's. */
    retry: false,
    queryFn: () => client.raw().rpcScalar<AuthoringTarget>('form_layout_at_target', {
      p_entity: entity, p_persona: persona, p_host: host,
    }, 'core'),
  })
}
